<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import QRCode from 'qrcode';
import { useI18n } from 'vue-i18n';
import AuthService from '@/services/api/AuthService';
import router from '@/router';
import { useUiStore, useAuthStore } from '@/stores';
import { toastServiceError } from '@/services';

const { t } = useI18n();

const qrCodeUrl = ref('');

const formState = ref({
    code: '',
    remember: false,
});

const uiStore = useUiStore();
const authStore = useAuthStore();

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const twoFactorEnabled = computed(() => authStore.user && authStore.user.twoFactorEnabled);

const twoFaSetup = async () => {
    try {
        const response = await AuthService.twoFaSetup();
        qrCodeUrl.value = await QRCode.toDataURL(response.data.otpAuthUrl);
    } catch (err) {
        await router.push('/login');
        console.error(err);
    }
};

const twoFaVerifySetup = async () => {
    if (!formState.value.code) {
        toastServiceError(t('authentication.2fa.error.enterCode'));
        return;
    }
    try {
        setLoading(true);
        const { data } = await AuthService.twoFaVerifySetup({
            code: formState.value.code,
            remember: formState.value.remember,
        });
        authStore.login(data);

        await router.push('/');
    } catch (error) {
        console.log(error);
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const twoFaVerify = async () => {
    if (!formState.value.code) {
        toastServiceError(t('authentication.2fa.error.enterCode'));
        return;
    }
    try {
        setLoading(true);
        const { data } = await AuthService.twoFaVerify({
            code: formState.value.code,
            remember: formState.value.remember,
        });

        authStore.login(data);
        await router.push('/');
    } catch (error) {
        console.log(error);
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const verify = async () => {
    if (twoFactorEnabled.value) {
        await twoFaVerify();
    } else {
        await twoFaVerifySetup();
    }
};

onMounted(async () => {
    if (!twoFactorEnabled.value) {
        await twoFaSetup();
    }
});
</script>

<template>
    <div class="grid w-full grid-cols-1 sm:grid-cols-2">
        <div class="bg-primary relative hidden w-full items-center justify-center sm:flex">
            <span class="login-bg h-[150px] xl:h-[250px]" />
        </div>
        <div
            class="flex w-full flex-col items-center bg-gray-50 px-8 pt-20 sm:items-start sm:justify-center sm:pt-0 lg:px-28 xl:px-40"
        >
            <span class="login-logo mb-8 w-[225px]" />

            <h3 class="mb-4 text-center text-2xl font-semibold text-gray-700">
                {{ t('authentication.2fa.title') }}
            </h3>

            <p class="mb-6 max-w-xl text-center text-sm">
                {{ t('authentication.2fa.description') }}
            </p>

            <div v-if="qrCodeUrl" class="mb-4 flex w-full justify-center">
                <img :src="qrCodeUrl" alt="2FA QR Code" class="h-48 w-48" />
            </div>

            <div class="flex w-full max-w-sm flex-col items-center gap-3">
                <input
                    v-model="formState.code"
                    type="text"
                    inputmode="numeric"
                    maxlength="6"
                    placeholder="123456"
                    @keydown.enter="verify"
                    class="w-full rounded border border-gray-300 px-3 py-2 text-center text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                <label class="flex items-center gap-2 self-start text-sm text-gray-600">
                    <input type="checkbox" v-model="formState.remember" />
                    <span>{{ t('authentication.2fa.form.rememberMe') }}</span>
                </label>

                <button
                    class="w-full rounded bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                    :disabled="!formState.code"
                    @click="verify"
                >
                    {{ t('authentication.2fa.form.verify') }}
                </button>
            </div>
        </div>
    </div>
    <UiLoadingDialog :show="uiStore.isLoading" :text="$t('ui-loading-dialog-text')" />
</template>
